/**
 * Palette strip that shows layer band coverage as color segments.
 * - Inline percentage label is rendered on any swatch wider than 10%.
 * - Hover pops up a card with the layer's name and the ACTIVE-metric percentage
 *   emphasised, plus supporting details.
 * - No inline water warning here — that's rendered on the layer row itself.
 */
export const PaletteStrip = ({
  layers,
  mapCoverage,
  layerCoverage,
  histogram,
  metric,
  onMetricChange,
}) => {
  const hasMap = mapCoverage && mapCoverage.some((c) => c > 0);
  const useMap = metric === "map" && hasMap;
  const percentages = useMap ? mapCoverage : layerCoverage;

  const widths = percentages.map((c) => Math.max(0.01, c));
  const total = widths.reduce((s, v) => s + v, 0) || 1;
  const maxHist = histogram ? Math.max(1, ...histogram) : 1;

  const segments = layers.map((l, i) => ({
    layer: l,
    widthPct: (widths[i] / total) * 100,
    activePct: percentages[i] ?? 0,
    mapPct: mapCoverage[i] ?? 0,
    layerPct: layerCoverage[i] ?? 0,
  }));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[10px] text-zinc-500 tracking-widest uppercase font-medium">
        <div className="flex items-center gap-1 bg-[#0d0e12] rounded-md p-0.5 border border-zinc-800/70">
          <MetricBtn
            testId="metric-layer"
            active={metric === "layer"}
            onClick={() => onMetricChange("layer")}
            label="Layer %"
          />
          <MetricBtn
            testId="metric-map"
            active={metric === "map"}
            onClick={() => onMetricChange("map")}
            label="Map Coverage %"
          />
        </div>
        <span className="mono">
          {useMap ? "Map Coverage" : "Layer Range"}
        </span>
      </div>

      <div className="relative rounded-lg border border-zinc-800">
        {/* Visual band — clipped + rounded */}
        <div className="relative rounded-lg overflow-hidden">
          {histogram && (
            <div className="absolute inset-0 flex items-end pointer-events-none opacity-40">
              {Array.from({ length: 256 }).map((_, i) => (
                <div
                  key={i}
                  className="flex-1 bg-zinc-500/70"
                  style={{
                    height: `${(histogram[i] / maxHist) * 100}%`,
                    minWidth: 1,
                  }}
                />
              ))}
            </div>
          )}
          <div className="relative flex h-11">
            {segments.map(({ layer, widthPct, activePct }) => (
              <div
                key={layer.id}
                data-testid={`palette-swatch-${layer.name}`}
                className="relative flex items-center justify-center select-none overflow-hidden"
                style={{ width: `${widthPct}%`, backgroundColor: layer.color }}
              >
                {activePct >= 10 && (
                  <span
                    data-testid={`palette-inline-pct-${layer.name}`}
                    className="mono text-[11px] font-semibold text-white pointer-events-none"
                    style={{ textShadow: "0 1px 2px rgba(0,0,0,0.65)" }}
                  >
                    {Math.round(activePct)}%
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Hover overlay — NOT clipped, so the percentage popover can escape upward */}
        <div className="absolute inset-0 flex h-11">
          {segments.map(({ layer, widthPct, activePct, mapPct, layerPct }) => (
            <div
              key={layer.id}
              data-testid={`palette-hover-${layer.name}`}
              className="group relative h-full cursor-help"
              style={{ width: `${widthPct}%` }}
            >
              {/* Popup with pop-up animation */}
              <div
                data-testid={`palette-popover-${layer.name}`}
                className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 origin-bottom
                           opacity-0 scale-75 pointer-events-none
                           group-hover:opacity-100 group-hover:scale-100
                           transition-all duration-200 ease-out
                           z-50"
              >
                <div className="bg-[#141519] border border-zinc-700 rounded-lg px-3 py-2 shadow-xl shadow-black/50 min-w-[170px]">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="w-3.5 h-3.5 rounded border border-zinc-700"
                      style={{ backgroundColor: layer.color }}
                    />
                    <span className="text-xs font-semibold text-zinc-100">
                      {layer.name}
                    </span>
                  </div>
                  <div
                    data-testid={`palette-popover-pct-${layer.name}`}
                    className="mono text-2xl font-bold text-white leading-none mb-1.5"
                  >
                    {activePct.toFixed(2)}%
                  </div>
                  <div className="text-[10px] mono text-zinc-400 space-y-0.5">
                    <div>Range: {layer.min}–{layer.max}</div>
                    {hasMap && (
                      <div>
                        Map:{" "}
                        <span className="text-zinc-100">
                          {mapPct.toFixed(2)}%
                        </span>
                      </div>
                    )}
                    <div>
                      Layer:{" "}
                      <span className="text-zinc-100">
                        {layerPct.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
                <div className="w-2 h-2 bg-[#141519] border-r border-b border-zinc-700 rotate-45 mx-auto -mt-1" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MetricBtn = ({ active, onClick, label, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all duration-200 ease-out hover:-translate-y-px active:scale-95 ${
      active
        ? "brand-gradient text-white shadow-sm shadow-rose-500/25"
        : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
    }`}
  >
    {label}
  </button>
);
